/**
 * The webhook contract, as documented on the public `/webhooks` page.
 *
 * SINGLE SOURCE, AND IT IS TESTED ACROSS THE LANGUAGE BOUNDARY.
 * `backend/tests/test_webhooks.py` parses the field names out of this file and asserts
 * they are exactly the keys `domain.member_payload.flat` produces. Adding a field to the
 * payload without documenting it here fails the build, and vice versa — which is the only
 * thing that stops a schema page quietly becoming fiction. A sample or a table that has
 * fallen behind what we actually send is worse than none: somebody writes a receiver
 * against it and finds out in production.
 */

export interface Field {
  name: string;
  type: 'string' | 'integer' | 'number' | 'boolean';
  note: string;
}

export interface FieldGroup {
  title: string;
  blurb: string;
  fields: Field[];
}

/** Every key in `data`, except the numbered intake answers — see INTAKE. */
export const FIELD_GROUPS: FieldGroup[] = [
  {
    title: 'Community',
    blurb: 'Which community this happened in.',
    fields: [
      { name: 'community_id', type: 'integer', note: 'Our internal id. Stable.' },
      { name: 'community_slug', type: 'string', note: 'The Skool slug, e.g. my-community-1234.' },
      { name: 'community_name', type: 'string', note: 'Display name.' },
    ],
  },
  {
    title: 'Who they are',
    blurb: 'Identity, from the member’s Skool profile.',
    fields: [
      { name: 'member_id', type: 'integer', note: 'Our internal id for this person in THIS community. Stable — use it as your key.' },
      { name: 'member_external_id', type: 'string', note: 'Skool’s own 32-char member id.' },
      { name: 'name', type: 'string', note: 'Display name, or the handle when Skool has no real name.' },
      { name: 'first_name', type: 'string', note: 'May be null.' },
      { name: 'last_name', type: 'string', note: 'May be null.' },
      { name: 'handle', type: 'string', note: 'Skool @handle.' },
      { name: 'profile_url', type: 'string', note: 'Link to their Skool profile.' },
      { name: 'avatar_url', type: 'string', note: 'May be null.' },
      { name: 'bio', type: 'string', note: 'May be null.' },
      { name: 'location', type: 'string', note: 'Free text they typed. May be null.' },
      { name: 'time_zone', type: 'string', note: 'IANA zone, e.g. Europe/Lisbon. May be null.' },
    ],
  },
  {
    title: 'How to reach them',
    blurb:
      'Skool gives a moderator no member email at all, so `email` is RESOLVED from the ' +
      'sources below, in that order. In most communities the only address we hold is the ' +
      'one the member typed into the join questions.',
    fields: [
      { name: 'email', type: 'string', note: 'Resolved: contact_email → billing_email → survey_email. Null when we hold none.' },
      { name: 'contact_email', type: 'string', note: 'Typed by the community owner. Almost always null.' },
      { name: 'billing_email', type: 'string', note: 'Skool’s Stripe address. Only where we have staff access to the billing tabs.' },
      { name: 'survey_email', type: 'string', note: 'Pulled from the join answers. This is usually the one that has a value.' },
      { name: 'phone', type: 'string', note: 'Owner-typed. Almost always null.' },
    ],
  },
  {
    title: 'Membership standing',
    blurb:
      'The lifecycle fields. Read the “What the values mean” table below before ' +
      'branching on any of these — one of them does not mean what its name suggests.',
    fields: [
      { name: 'status', type: 'string', note: 'active | cancelling | churned | left | removed | banned | null. THE membership state.' },
      { name: 'mrr_status', type: 'string', note: 'NOT billing, despite the name — an activity badge. See the warning below.' },
      { name: 'skool_role', type: 'string', note: 'member | admin | owner | moderator. Staff role, not a plan.' },
      { name: 'joined_at', type: 'string', note: 'UTC ISO-8601. When Skool approved them.' },
      { name: 'cancelled_at', type: 'string', note: 'UTC ISO-8601. When they hit cancel. Null if they never did.' },
      { name: 'declined_at', type: 'string', note: 'UTC ISO-8601. Last declined payment. Null if none.' },
      { name: 'ends_at', type: 'string', note: 'UTC ISO-8601. When a cancelled membership actually lapses — they keep access until then.' },
      { name: 'departed_at', type: 'string', note: 'UTC ISO-8601. When they actually left. Null while still a member.' },
    ],
  },
  {
    title: 'What they pay',
    blurb:
      'Only populated where we have staff access to the membership billing tabs. Money is ' +
      'in MINOR UNITS — 4900 is $49.00.',
    fields: [
      { name: 'plan_tier', type: 'string', note: 'standard | premium | vip — the owner’s tier name. INDEPENDENT of price: a tier can be comped at 0.' },
      { name: 'plan_amount_cents', type: 'integer', note: 'Recurring price in minor units. Null on free/comped.' },
      { name: 'plan_currency', type: 'string', note: 'e.g. usd.' },
      { name: 'plan_interval', type: 'string', note: 'month | year.' },
      { name: 'ltv_cents', type: 'integer', note: 'Lifetime value to date, minor units. Null or 0 means they have NEVER been charged.' },
      { name: 'first_paid_at', type: 'string', note: 'UTC ISO-8601. When their first payment cleared. Null = never paid — this is how a trial is recognised.' },
    ],
  },
  {
    title: 'Where they came from',
    blurb: 'Join attribution, where Skool records it.',
    fields: [
      { name: 'invited_by', type: 'string', note: 'Skool user id of the inviter. May be null.' },
      { name: 'attribution', type: 'string', note: 'e.g. invite. May be null.' },
    ],
  },
  {
    title: 'How they contribute',
    blurb:
      'Our own engagement scoring, recomputed roughly every 15 minutes. Every field here ' +
      'is null for a member we have never scored — a null is “unmeasured”, not zero.',
    fields: [
      { name: 'posts', type: 'integer', note: 'Posts written.' },
      { name: 'comments', type: 'integer', note: 'Comments written.' },
      { name: 'activity', type: 'integer', note: 'Composite activity score.' },
      { name: 'upvotes_received', type: 'integer', note: 'Upvotes their content earned.' },
      { name: 'replies_earned', type: 'integer', note: 'Replies their posts drew from others.' },
      { name: 'momentum', type: 'number', note: '−1 to 1. Recent engagement vs the prior window.' },
      { name: 'stage', type: 'string', note: 'Lifecycle bucket: new | rising | steady | cooling | at_risk | dormant | inactive | departed.' },
      { name: 'first_active_at', type: 'string', note: 'UTC ISO-8601. First contribution.' },
      { name: 'last_active_at', type: 'string', note: 'UTC ISO-8601. Most recent activity.' },
      { name: 'contributor_role', type: 'string', note: 'key_voice | quiet_expert | broadcaster | spectator | newcomer | departed. ENGAGEMENT, not billing.' },
      { name: 'contributor_role_previous', type: 'string', note: 'What they moved away from. Null until their role has moved once.' },
      { name: 'contributor_role_changed_at', type: 'string', note: 'UTC ISO-8601 of that move.' },
    ],
  },
  {
    title: 'Join answers',
    blurb:
      'What the member typed into the community’s join questions. Numbered rather than ' +
      'keyed by the question text, because the questions are the owner’s own free text and ' +
      'change whenever they edit the form.',
    fields: [
      { name: 'intake_count', type: 'integer', note: 'How many answers follow. 0 when they answered none.' },
    ],
  },
];

/** The numbered triple, repeated `intake_count` times. Not literal keys. */
export const INTAKE = [
  { name: 'intake_N_question', type: 'string', note: 'The question, exactly as the owner wrote it.' },
  { name: 'intake_N_type', type: 'string', note: 'Skool’s input type: text | email | options | …' },
  { name: 'intake_N_answer', type: 'string', note: 'What they typed. Null if left blank.' },
];

export interface EventDoc {
  name: string;
  label: string;
  when: string;
  repeats: string;
}

/** Every event that exists today. There are three. */
export const EVENTS: EventDoc[] = [
  {
    name: 'member.joined',
    label: 'A member joins',
    when: 'Skool approved them into the community.',
    repeats: 'Once per member, ever.',
  },
  {
    name: 'member.left',
    label: 'A member leaves',
    when: 'They left, were removed, were banned, or churned out of billing.',
    repeats: 'Once per member, ever.',
  },
  {
    name: 'member.converted_to_paid',
    label: 'A free trial converts to paid',
    when:
      'Their first payment cleared — first_paid_at was stamped. Skool marks nobody as ' +
      'trialling, so this is the only observable instant at which a trial becomes a ' +
      'customer.',
    repeats: 'Once per member, ever.',
  },
  {
    name: 'member.trial_payment_failed',
    label: 'A trial payment is declined (they never paid)',
    when:
      'A payment was declined for someone whose ltv_cents is still null or 0 — they have ' +
      'never been charged. This deliberately EXCLUDES an existing paying member whose ' +
      'card later fails; that is a different situation and must not cost them access.',
    repeats: 'Every decline. A fixed card clears the flag, so a later failure fires again.',
  },
  {
    name: 'member.role_changed',
    label: 'A member’s contributor role changes',
    when:
      'Their ENGAGEMENT classification moved — e.g. spectator → key_voice. This is about ' +
      'how much they post and how well it lands. It has nothing to do with payment.',
    repeats: 'Every time it moves, for as long as they are a member.',
  },
];

/** What we cannot tell you yet. Stated plainly so nobody builds against a gap. */
export const NOT_AVAILABLE = [
  'A member CANCELLING (the cancellation itself — you still see cancelled_at on the next event).',
  'A plan or tier changing.',
  'An existing paying member’s card failing — only a NEVER-PAID decline has an event today.',
];

export interface LifecycleRow {
  scenario: string;
  answer: string;
  fields: string;
  supported: boolean;
}

/** How to recognise each real-world state from the fields above. */
export const LIFECYCLE: LifecycleRow[] = [
  {
    scenario: 'An active free trial',
    answer: 'A priced plan they have never been charged for.',
    fields:
      'plan_amount_cents > 0 AND (ltv_cents is null or 0) AND first_paid_at is null. Skool ' +
      'marks nobody as trialling — none of the four membership shapes is a trial — so it is ' +
      'derived: they hold a plan and no money has moved.',
    supported: true,
  },
  {
    scenario: 'A converted paying member',
    answer: 'Money has actually moved.',
    fields:
      'ltv_cents > 0 and first_paid_at set. Fires member.converted_to_paid the moment the ' +
      'first charge clears. Note a plan price alone does NOT mean they have paid — that is ' +
      'the trial case above.',
    supported: true,
  },
  {
    scenario: 'Cancelled, access not ended yet',
    answer: 'status = cancelling.',
    fields:
      'status="cancelling", cancelled_at set, and ends_at in the FUTURE. They are still a ' +
      'member until ends_at — keep their access until then.',
    supported: true,
  },
  {
    scenario: 'Fully churned',
    answer: 'status is one of the departed set.',
    fields:
      'status ∈ {churned, left, removed, banned} AND departed_at set. churned specifically ' +
      'means billing churn — payment failed out. left = walked away, removed/banned = staff.',
    supported: true,
  },
  {
    scenario: 'A declined trial payment',
    answer: 'A decline from someone who has never paid.',
    fields:
      'declined_at set AND (ltv_cents null or 0). Fires member.trial_payment_failed. This ' +
      'is the one to act on: they entered a card, the first charge failed, and no money ' +
      'ever moved.',
    supported: true,
  },
  {
    scenario: 'A declined payment from an existing paid member',
    answer: 'declined_at set; status does not move on its own.',
    fields:
      'declined_at is set while status stays active or cancelling. Skool only moves them to ' +
      'churned once they have actually failed out. Do not revoke access on declined_at alone.',
    supported: true,
  },
];
